/**
 * Session-only store for the External Brand Proposals tab.
 *
 * Plain module-level state — no `sessionStorage`, no `localStorage`, no
 * Firestore. It lives exactly as long as the page does and is gone on tab
 * close, by design: the only durable record of a review is the
 * `externalRefs.microbrandAtlas` stamp on the WK brand doc.
 *
 * It is module-level rather than component state because the dashboard
 * UNMOUNTS a tab's component when another tab is selected, and Create/Update
 * hand off to the Curated Watches tab. Anything held in React state would be
 * lost on that round trip, taking the list, the open brand and the reviewer's
 * filters with it. For the same reason subscription is `useSyncExternalStore`
 * rather than a Context provider — there is no component that outlives the
 * hand-off to host one.
 */
import { useSyncExternalStore } from 'react';
import type { MaBrandDetail, MaBrandRow } from '../../services/microbrandAtlas.types';
import type { WatchBrand } from '../../types';
import type { DetailSource, PlatformCheck } from './proposalCachePolicy';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export type ListStatusFilter =
  | 'unmatched'
  | 'matched'
  | 'session-created'
  | 'session-updated'
  | 'session-dismissed'
  | 'all';

export interface ProposalsUiState {
  view: 'list' | 'detail';
  selectedSlug: string | null;
  search: string;
  statusFilter: ListStatusFilter;
  /** Lowercased `ecommerce_platform`, or null for every platform. */
  platformFilter: string | null;
  founderManagedOnly: boolean;
  page: number;
  pageSize: number;
}

export const defaultProposalsUiState: ProposalsUiState = {
  view: 'list',
  selectedSlug: null,
  search: '',
  statusFilter: 'unmatched',
  platformFilter: null,
  founderManagedOnly: false,
  page: 1,
  pageSize: 50,
};

export interface DetailEntry {
  status: LoadStatus;
  /** `null` once loaded means MA has no published brand at this slug. */
  detail: MaBrandDetail | null;
  error: string | null;
  /** Where `detail` came from — live MA, the durable cache, or a stale cached copy. */
  source?: DetailSource;
  /** When `detail` was fetched from MA (cache age). */
  fetchedAt?: Date | null;
  /** Why a stale cached copy is shown instead of a live fetch. */
  liveProblem?: string | null;
  /** The last "Verify platform" result, persisted on the cache doc. */
  platformCheck?: PlatformCheck | null;
}

interface SessionCacheState {
  listStatus: LoadStatus;
  listError: string | null;
  /** Set when the env vars are missing — Retry cannot fix that. */
  listErrorIsConfig: boolean;
  rows: MaBrandRow[];
  /** Non-fatal: the list is usable, the platform/founder filters are not. */
  prefetchError: string | null;
  listFetchedAt: number | null;
  wkStatus: LoadStatus;
  wkError: string | null;
  wkBrands: WatchBrand[];
  /** Bumped on every detail-map mutation so selectors re-run. */
  detailVersion: number;
  ui: ProposalsUiState;
}

/** Enough for a long review session; eviction only bites past this many opens. */
export const DETAIL_LRU_SIZE = 200;

let state: SessionCacheState = {
  listStatus: 'idle',
  listError: null,
  listErrorIsConfig: false,
  rows: [],
  prefetchError: null,
  listFetchedAt: null,
  wkStatus: 'idle',
  wkError: null,
  wkBrands: [],
  detailVersion: 0,
  ui: defaultProposalsUiState,
};

/** Insertion-ordered, so the first key is always the least recently used. */
const details = new Map<string, DetailEntry>();

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSessionCache(): SessionCacheState {
  return state;
}

export function patchSessionCache(patch: Partial<SessionCacheState>) {
  state = { ...state, ...patch };
  emit();
}

export function patchProposalsUi(patch: Partial<ProposalsUiState>) {
  state = { ...state, ui: { ...state.ui, ...patch } };
  emit();
}

export function getDetailEntry(slug: string): DetailEntry | undefined {
  return details.get(slug);
}

/**
 * Write a detail entry and mark it most-recently-used. Callers must NOT store
 * failures as `ready` — an errored entry is kept only so the panel can render
 * the message, and {@link dropDetailEntry} removes it on Retry.
 */
export function setDetailEntry(slug: string, entry: DetailEntry) {
  details.delete(slug);
  details.set(slug, entry);
  while (details.size > DETAIL_LRU_SIZE) {
    const oldest = details.keys().next().value;
    if (oldest === undefined) break;
    details.delete(oldest);
  }
  state = { ...state, detailVersion: state.detailVersion + 1 };
  emit();
}

/** Mark an entry as just used, without notifying (reads must not re-render). */
export function touchDetailEntry(slug: string) {
  const entry = details.get(slug);
  if (!entry) return;
  details.delete(slug);
  details.set(slug, entry);
}

export function dropDetailEntry(slug: string) {
  if (!details.delete(slug)) return;
  state = { ...state, detailVersion: state.detailVersion + 1 };
  emit();
}

export function clearDetailEntries() {
  details.clear();
  // A refresh re-pulls MA, so ticks made against the old values go with it.
  reviewDrafts.clear();
  state = { ...state, detailVersion: state.detailVersion + 1 };
  emit();
}

/** Replace (or append) one WK brand after a Create/Update lands. */
export function upsertWkBrand(brand: WatchBrand) {
  const exists = state.wkBrands.some((b) => b.id === brand.id);
  state = {
    ...state,
    wkBrands: exists
      ? state.wkBrands.map((b) => (b.id === brand.id ? brand : b))
      : [...state.wkBrands, brand],
  };
  emit();
}

/**
 * The reviewer's ticks and inline edits for one comparison, kept so they
 * survive this tab being unmounted. Without it, cancelling out of BrandEditor
 * and coming back silently put every tick back to its default — and the next
 * Create or Update then wrote fields the reviewer had deliberately excluded.
 */
export interface ReviewDraft {
  ticks: Record<string, boolean>;
  edits: Record<string, string>;
}

const reviewDrafts = new Map<string, ReviewDraft>();

/** A draft belongs to one MA brand compared against one WK brand (or none). */
export function reviewDraftKey(maSlug: string, wkBrandId: string | null | undefined): string {
  return `${maSlug}|${wkBrandId ?? ''}`;
}

export function getReviewDraft(key: string): ReviewDraft | undefined {
  return reviewDrafts.get(key);
}

/** Not subscribed to: the detail view owns the live state and writes through. */
export function setReviewDraft(key: string, draft: ReviewDraft) {
  reviewDrafts.set(key, draft);
}

/**
 * Forget every draft for an MA brand. Called once a write lands: WK's values
 * have changed, so the old ticks no longer describe the comparison on screen.
 */
export function clearReviewDrafts(maSlug: string) {
  for (const key of Array.from(reviewDrafts.keys())) {
    if (key.startsWith(`${maSlug}|`)) reviewDrafts.delete(key);
  }
}

/** Subscribe a component to a slice of the store. */
export function useSessionCache<T>(selector: (s: SessionCacheState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(state));
}

/** Test seam: put the store back to a cold start. */
export function resetSessionCacheForTests() {
  details.clear();
  reviewDrafts.clear();
  state = {
    listStatus: 'idle',
    listError: null,
    listErrorIsConfig: false,
    rows: [],
    prefetchError: null,
    listFetchedAt: null,
    wkStatus: 'idle',
    wkError: null,
    wkBrands: [],
    detailVersion: 0,
    ui: defaultProposalsUiState,
  };
  emit();
}
